import { useState } from "react";

/**
 * Gets the root domain for cookie storage (e.g., "example.com" from "sub.example.com")
 */
const getDomain = (): string => {
  const hostname = window.location.hostname;
  const parts = hostname.split(".");
  const subdomain = parts?.[parts.length - 2];
  const domain = parts?.[parts.length - 1];
  return (subdomain ? subdomain + "." : "") + domain;
};

const getItem = (key: string): string =>
  document.cookie.split("; ").reduce((total, currentCookie) => {
    const item = currentCookie.split("=");
    const storedKey = item[0];
    const storedValue = item[1];
    return key === storedKey ? decodeURIComponent(storedValue) : total;
  }, "");

/**
 * Sets a cookie with the given key, value, and expiration
 */
const setItem = (key: string, value: string, numberOfDays: number): void => {
  const now = new Date();
  // set the time to be now + numberOfDays
  now.setTime(now.getTime() + numberOfDays * 60 * 60 * 24 * 1000);
  document.cookie = `${key}=${value}; expires=${now.toUTCString()}; path=/; domain=.${getDomain()}`;
};

/**
 * Hook for reading and writing browser cookies with React state sync.
 * Cookies are stored on the root domain for cross-subdomain access.
 *
 * @param key - Cookie name
 * @param defaultValue - Default value if cookie doesn't exist
 * @returns Tuple of [value, setter]
 *
 * @example
 * const [locale, setLocale] = useCookie('locale', 'en');
 * setLocale('es', 365); // Set cookie for 1 year
 */
const useCookie = (key: string, defaultValue: string): [string, (value: string, numberOfDays: number) => void] => {
  const getCookie = () => getItem(key) || defaultValue;
  const [cookie, setCookie] = useState(getCookie());

  const updateCookie = (value: string, numberOfDays: number): void => {
    setCookie(value);
    setItem(key, value, numberOfDays);
  };

  return [cookie, updateCookie];
};

export default useCookie;
